import * as ko from 'knockout'
import { dayjs } from '@mobi/utils/date'
import type { BettingInformation } from '@classic/Betting-v2/Model/BettingInformation'
import { Disposable } from '@classic/AppUtils/Framework/Disposable/Disposable'
import type ObservableRaceKey from '@classic/Betting-v2/Model/Observables/ObservableRaceKey'
import { RaceCountdown } from '@core/Components/RaceCountdown'

export interface RaceSubHeaderViewModelParams {
  race: ObservableRaceKey
  context: { bettingContext: BettingInformation; legVisible: ko.Observable<number> }
}
export class RaceSubHeaderViewModel extends Disposable {
  public race: ObservableRaceKey
  public formattedStartTime: ko.PureComputed<string>
  public bettingContext: BettingInformation
  public legVisible: ko.Observable<number>
  public raceSelectionString: ko.Observable<string>
  public showTapToSelect: ko.PureComputed<boolean>
  public isCurrentRace: ko.PureComputed<boolean>
  public RaceCountdown = RaceCountdown

  constructor(params: RaceSubHeaderViewModelParams) {
    super()

    this.race = params.race
    this.legVisible = params.context.legVisible
    this.bettingContext = params.context.bettingContext

    this.raceSelectionString = ko.observable<string>('')

    this.formattedStartTime = ko.pureComputed<string>(() => {
      const rt = dayjs(params.race.startTime())
      return `${rt.format('HH:mm')}`
    })

    this.showTapToSelect = ko.pureComputed<boolean>((): boolean => {
      return this.raceSelectionString().length === 0 && this.race.leg() !== this.legVisible()
    })

    this.isCurrentRace = ko.pureComputed<boolean>(() => {
      return this.race.leg() === this.legVisible()
    })

    this.registerDisposals(() => {
      this.formattedStartTime.dispose()
      if (this.selectionsChangedSubscription != undefined) {
        this.selectionsChangedSubscription.dispose()
      }
      if (this.selectedPoolSubscription !== undefined) {
        this.selectedPoolSubscription.dispose()
      }
      this.showTapToSelect.dispose()
    })

    this.registerHandlers()
  }

  private registerHandlers() {
    const raceSelectionsObject = this.bettingContext.selections
      .selections()
      .find(s => s.raceKey().raceNumber() === this.race.raceNumber())

    if (raceSelectionsObject != undefined) {
      this.selectionsChangedSubscription = raceSelectionsObject.starters.subscribe(() => {
        this.raceSelectionString(this.selectionStringDisplay())
      })

      this.raceSelectionString(this.selectionStringDisplay())
    }

    if (
      this.bettingContext.selectedBetType().isAllUp() &&
      this.race.poolInfo.selectedPool !== undefined
    ) {
      this.selectedPoolSubscription = this.race.poolInfo.selectedPool.subscribe(() => {
        this.raceSelectionString(this.selectionStringDisplay())
      })

      this.raceSelectionString(this.selectionStringDisplay())
    }
  }

  private selectionStringDisplay() {
    const result = this.bettingContext
      .selectedBetType()
      .selectionStringProcessor.selections(
        this.bettingContext,
        this.race.raceNumber(),
        this.bettingContext.selections
      )
    const leadText =
      result.selectionStrings[0].length > 0 &&
      this.bettingContext.selectedBetType().isAllUp() &&
      this.race.poolInfo.selectedPool() !== undefined
        ? this.race.poolInfo.selectedPool().description() + ' - '
        : ''
    return leadText + result.selectionStrings[0]
  }

  public toggle(): void {
    if (this.legVisible() != this.race.leg()) {
      this.legVisible(this.race.leg())
    } else {
      this.legVisible(-1)
    }
  }

  private selectedPoolSubscription!: ko.Subscription
  private selectionsChangedSubscription!: ko.Subscription
}
